/**
 * StatsView - Vista principal de estadísticas
 *
 * Muestra métricas de completado, patrones y tendencias.
 * Diseñado para entender patrones, no para castigar.
 */

import { useEffect, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Clock,
  Flame,
  Target,
  Sun,
  Moon,
  Sunrise,
} from 'lucide-react';
import { useHistoryStore } from '@/store';
import type { PeriodStats } from '@/core/types';
import { cn } from '@/core/utils';
import { SkeletonStats } from '@/ui/primitives';
import { ProgressHeatmap } from './ProgressHeatmap';

type Period = 'week' | 'month' | 'quarter' | 'year';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Año' },
];

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const TIME_BLOCK_ICONS = {
  morning: Sunrise,
  afternoon: Sun,
  evening: Moon,
};

const TIME_BLOCK_LABELS = {
  morning: 'Mañana',
  afternoon: 'Tarde',
  evening: 'Noche',
};

export function StatsView() {
  const [period, setPeriod] = useState<Period>('week');
  const [stats, setStats] = useState<PeriodStats | null>(null);
  const { calculatePeriodStats, isLoading, getStreak } = useHistoryStore();
  const [currentStreak, setCurrentStreak] = useState(0);

  useEffect(() => {
    const loadStats = async () => {
      const data = await calculatePeriodStats(period);
      setStats(data);
      const streak = await getStreak();
      setCurrentStreak(streak);
    };
    loadStats();
  }, [period, calculatePeriodStats, getStreak]);

  if (isLoading) {
    return <SkeletonStats />;
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto text-slate-600 mb-4" />
        <p className="text-slate-400">No hay datos suficientes para este período.</p>
        <p className="text-sm text-slate-500 mt-2">
          Completa algunos protocolos para ver tus estadísticas.
        </p>
      </div>
    );
  }

  const getTrendIcon = (current: number, threshold = 50) => {
    if (current >= threshold + 10) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (current <= threshold - 10) return <TrendingDown className="w-4 h-4 text-rose-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const TimeBlockIcon = TIME_BLOCK_ICONS[stats.bestTimeBlock];

  return (
    <div className="space-y-6">
      {/* Selector de período */}
      <div className="flex gap-1 p-1 bg-slate-900 rounded-lg">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={cn(
              'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              period === p.value
                ? 'bg-slate-800 text-slate-100'
                : 'text-slate-400 hover:text-slate-200'
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-2 gap-3">
        {/* Racha actual */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-slate-400">Racha actual</span>
          </div>
          <div className="text-2xl font-bold text-orange-400">
            {currentStreak} <span className="text-sm font-normal text-slate-500">días</span>
          </div>
          {stats.bestStreak > currentStreak && (
            <p className="text-xs text-slate-500 mt-1">
              Mejor: {stats.bestStreak} días
            </p>
          )}
        </div>

        {/* Completado promedio */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-slate-400">Promedio</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-blue-400">{stats.avgCompletionRate}%</span>
            {getTrendIcon(stats.avgCompletionRate)}
          </div>
        </div>

        {/* Días activos */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-slate-400">Días activos</span>
          </div>
          <div className="text-2xl font-bold text-violet-400">
            {stats.activeDays} <span className="text-sm font-normal text-slate-500">/ {stats.totalDays}</span>
          </div>
        </div>

        {/* Tiempo invertido */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-slate-400">Tiempo/día</span>
          </div>
          <div className="text-2xl font-bold text-emerald-400">
            {stats.avgDailyTime} <span className="text-sm font-normal text-slate-500">min</span>
          </div>
        </div>
      </div>

      {/* Progreso por capas */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-4">Completado por capa</h3>
        <div className="space-y-3">
          <LayerProgress
            label="Supervivencia"
            percentage={stats.avgByLayer.survival}
            color="rose"
          />
          <LayerProgress
            label="Mantenimiento"
            percentage={stats.avgByLayer.maintenance}
            color="amber"
          />
          <LayerProgress
            label="Crecimiento"
            percentage={stats.avgByLayer.growth}
            color="emerald"
          />
        </div>
      </div>

      {/* Patrones */}
      <div className="grid grid-cols-2 gap-3">
        {/* Mejor día */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-slate-300 mb-2">Mejor día</h3>
          <div className="text-lg font-semibold text-emerald-400">
            {DAY_NAMES[stats.bestDayOfWeek]}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Más consistencia
          </p>
        </div>

        {/* Mejor bloque */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-slate-300 mb-2">Mejor momento</h3>
          <div className="flex items-center gap-2">
            <TimeBlockIcon className="w-5 h-5 text-amber-400" />
            <span className="text-lg font-semibold text-amber-400">
              {TIME_BLOCK_LABELS[stats.bestTimeBlock]}
            </span>
          </div>
        </div>
      </div>

      {/* Protocolos más consistentes */}
      {stats.mostConsistent.length > 0 && (
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-slate-300 mb-3">Más consistentes</h3>
          <div className="space-y-2">
            {stats.mostConsistent.slice(0, 3).map((p) => (
              <div key={p.protocolId} className="flex items-center justify-between">
                <span className="text-sm text-slate-400 truncate flex-1">{p.protocolTitle}</span>
                <span className="text-sm font-medium text-emerald-400 ml-2">
                  {p.completionRate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Protocolos que necesitan atención */}
      {stats.leastConsistent.length > 0 && stats.leastConsistent[0] && stats.leastConsistent[0].completionRate < 50 && (
        <div className="bg-slate-800/50 rounded-lg p-4 border border-amber-900/50">
          <h3 className="text-sm font-medium text-amber-300 mb-3">Necesitan atención</h3>
          <p className="text-xs text-slate-500 mb-3">
            No para castigarte. Para entender qué dificulta completarlos.
          </p>
          <div className="space-y-2">
            {stats.leastConsistent.slice(0, 3).map((p) => (
              <div key={p.protocolId} className="flex items-center justify-between">
                <span className="text-sm text-slate-400 truncate flex-1">{p.protocolTitle}</span>
                <span className="text-sm font-medium text-amber-400 ml-2">
                  {p.completionRate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Heatmap de actividad */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <ProgressHeatmap days={90} />
      </div>

      {/* Mensaje motivacional neurodivergente-friendly */}
      <div className="bg-slate-900/50 rounded-lg p-4 text-center">
        <p className="text-sm text-slate-400">
          {stats.avgCompletionRate >= 70
            ? 'Excelente consistencia. El sistema está funcionando.'
            : stats.avgCompletionRate >= 50
            ? 'Progreso sólido. Recuerda: consistencia > perfección.'
            : 'Cada día es una oportunidad nueva. Los datos ayudan a ajustar, no a juzgar.'}
        </p>
      </div>
    </div>
  );
}

function LayerProgress({
  label,
  percentage,
  color,
}: {
  label: string;
  percentage: number;
  color: 'rose' | 'amber' | 'emerald';
}) {
  const colorClasses = {
    rose: 'bg-rose-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
  };

  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-400">{label}</span>
        <span className="text-slate-300">{percentage}%</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', colorClasses[color])}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}
